export interface AnalyticsJobFailure {
  code?: string;
  retryable?: boolean;
  error?: string;
  detail?: string;
}

export function isRetryableAnalyticsJobFailure(data: AnalyticsJobFailure, status?: number): boolean {
  if (status === 401 || status === 403 || status === 404 || status === 410) return false;
  if (status === 429 || (status !== undefined && status >= 500 && status <= 599)) return true;
  return data.retryable === true || ["TTMA_UNAVAILABLE", "FINALIZATION_FAILED", "FINALIZATION_PENDING"].includes(data.code ?? "");
}

export class AnalyticsStreamHttpError extends Error {
  constructor(public readonly status: number, public readonly failure: AnalyticsJobFailure) {
    super(failure.detail ?? failure.error ?? `Server returned ${status}`);
  }
}

/** Read named SSE events without putting the browser identity in the URL. */
export async function readAnalyticsJobStream(
  url: string,
  options: {
    headers: Record<string, string>;
    signal: AbortSignal;
    timeoutMs: number;
    onEvent: (event: string, data: string) => boolean | void;
  },
): Promise<void> {
  const controller = new AbortController();
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  const abort = () => {
    controller.abort();
    void reader?.cancel().catch(() => { /* transport may already be closed */ });
  };
  options.signal.addEventListener("abort", abort, { once: true });
  if (options.signal.aborted) controller.abort();
  const timer = setTimeout(abort, Math.max(0, options.timeoutMs));
  try {
    const response = await fetch(url, {
      headers: { ...options.headers, Accept: "text/event-stream" },
      credentials: "include",
      signal: controller.signal,
    });
    if (!response.ok) {
      let failure: AnalyticsJobFailure = {};
      try { failure = await response.json(); } catch { /* use HTTP status */ }
      throw new AnalyticsStreamHttpError(response.status, failure);
    }
    if (!response.body || !response.headers.get("content-type")?.includes("text/event-stream")) {
      throw new Error("Analytics stream unavailable");
    }
    reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let event = "message";
    let data: string[] = [];
    let frameSize = 0;
    const maxFrameSize = 2 * 1024 * 1024;
    while (!controller.signal.aborted) {
      const chunk = await reader.read();
      if (controller.signal.aborted) return;
      if (chunk.done) throw new Error("Analytics stream closed before completion");
      buffer += decoder.decode(chunk.value, { stream: true });
      while (true) {
        const end = buffer.search(/[\r\n]/);
        if (end < 0 || (buffer[end] === "\r" && end === buffer.length - 1)) break;
        const line = buffer.slice(0, end);
        const separatorSize = buffer[end] === "\r" && buffer[end + 1] === "\n" ? 2 : 1;
        buffer = buffer.slice(end + separatorSize);
        frameSize += line.length;
        if (frameSize > maxFrameSize) throw new Error("Analytics stream frame too large");
        if (!line) {
          if (data.length && options.onEvent(event, data.join("\n")) === false) return;
          event = "message";
          data = [];
          frameSize = 0;
        } else if (!line.startsWith(":")) {
          const colon = line.indexOf(":");
          const field = colon < 0 ? line : line.slice(0, colon);
          const value = colon < 0 ? "" : line.slice(colon + 1).replace(/^ /, "");
          if (field === "event") event = value;
          if (field === "data") data.push(value);
        }
      }
      if (frameSize + buffer.length > maxFrameSize) throw new Error("Analytics stream frame too large");
    }
    if (!options.signal.aborted) throw new Error("Analytics stream timed out");
  } finally {
    clearTimeout(timer);
    options.signal.removeEventListener("abort", abort);
    controller.abort();
    if (reader) {
      try { await reader.cancel(); } catch { /* transport may already be closed */ }
      reader.releaseLock();
    }
  }
}
